import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { adService } from '../api';
import { KIND_LABELS, formatPrice, buildSpecs } from './AdCard';
import './AdCard.css';
import './MyAdsList.css';

const AD_STATUS_DRAFT = 0;
const AD_STATUS_PUBLISHED = 1;
const AD_STATUS_BLOCKED = 2;
const AD_STATUS_ARCHIVED = 3;

const MyAdsList = () => {
    const [ads, setAds] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [deletingId, setDeletingId] = useState(null);
    const [archivingId, setArchivingId] = useState(null);

    const load = useCallback(() => {
        setLoading(true);
        setError(null);
        adService.getMyAds()
            .then(({ data }) => setAds(data))
            .catch(() => setError('Не удалось загрузить объявления.'))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => { load(); }, [load]);

    const handleDelete = async (id) => {
        if (!window.confirm('Удалить объявление? Это действие необратимо.')) return;
        setDeletingId(id);
        try {
            await adService.deleteAd(id);
            setAds(prev => prev.filter(a => a.id !== id));
        } catch {
            alert('Не удалось удалить объявление.');
        } finally {
            setDeletingId(null);
        }
    };

    const handleArchiveToggle = async (ad) => {
        setArchivingId(ad.id);
        try {
            if (ad.status === AD_STATUS_ARCHIVED) {
                await adService.unarchiveAd(ad.id);
                setAds(prev => prev.map(a => a.id === ad.id ? { ...a, status: AD_STATUS_PUBLISHED } : a));
            } else {
                await adService.archiveAd(ad.id);
                setAds(prev => prev.map(a => a.id === ad.id ? { ...a, status: AD_STATUS_ARCHIVED } : a));
            }
        } catch {
            alert('Не удалось изменить статус объявления.');
        } finally {
            setArchivingId(null);
        }
    };

    if (loading) return <div className="profile-content__placeholder">Загрузка...</div>;
    if (error) return <div className="profile-content__placeholder">{error}</div>;
    if (ads.length === 0) return <div className="profile-content__placeholder">У вас пока нет объявлений.</div>;

    return (
        <div className="my-listings-list">
            {ads.map(ad => {
                const kindLabel = KIND_LABELS[ad.propertyKind] ?? ad.propertyKind;
                const specs = buildSpecs(ad);
                const address = ad.addressDisplay || ad.city || null;
                const photo = ad.photoUrls?.[0];
                const isDraft = ad.status === AD_STATUS_DRAFT;
                const isBlocked = ad.status === AD_STATUS_BLOCKED;
                const isArchived = ad.status === AD_STATUS_ARCHIVED;

                return (
                    <div key={ad.id} className="listing-card listing-card--list my-listing-card">
                        <Link to={`/ad/${ad.id}`} className="listing-card__photo-wrap my-listing-card__photo-link">
                            {photo
                                ? <img className="listing-card__photo" src={photo} alt={ad.title} />
                                : <div className="listing-card__photo--placeholder">Нет фото</div>
                            }
                        </Link>
                        <div className="listing-card__body">
                            <div className="listing-card__header">
                                <div className="listing-card__title-wrap">
                                    <Link to={`/ad/${ad.id}`} className="listing-card__title my-listing-card__title-link">
                                        {ad.title || kindLabel}
                                    </Link>
                                    {isDraft && (
                                        <span className="my-listing-card__badge my-listing-card__badge--draft">Черновик</span>
                                    )}
                                    {isBlocked && (
                                        <span className="my-listing-card__badge my-listing-card__badge--blocked">Заблокировано</span>
                                    )}
                                    {isArchived && (
                                        <span className="my-listing-card__badge my-listing-card__badge--archived">В архиве</span>
                                    )}
                                </div>
                                <div className="my-listing-card__actions">
                                    <Link to={`/edit/${ad.id}`} className="my-listing-card__action my-listing-card__action--edit">
                                        Редактировать
                                    </Link>
                                    {(ad.status === AD_STATUS_PUBLISHED || isArchived) && (
                                        <button
                                            type="button"
                                            className="my-listing-card__action"
                                            onClick={() => handleArchiveToggle(ad)}
                                            disabled={archivingId === ad.id}
                                        >
                                            {archivingId === ad.id ? '...' : (isArchived ? 'Восстановить' : 'В архив')}
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        className="my-listing-card__action my-listing-card__action--delete"
                                        onClick={() => handleDelete(ad.id)}
                                        disabled={deletingId === ad.id}
                                    >
                                        {deletingId === ad.id ? '...' : 'Удалить'}
                                    </button>
                                </div>
                            </div>
                            <span className="listing-card__price">{formatPrice(ad.price)}</span>
                            {specs && <span className="listing-card__specs">{specs}</span>}
                            {address && <span className="listing-card__address">{address}</span>}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default MyAdsList;
